import { QueryStatus } from "@reduxjs/toolkit/query";

import { emptySplitApi } from "./empty-split-api";
import { patientsApi } from "./patients";
import {
  EnsuredSessionReport,
  InstallNoahAppResponse,
  NoahAppConnectionsResponse,
  NoahDevice,
  NoahEventSubscriptionsResponse,
  NoahIntegrationRequest,
  NoahPatientResponse,
  NoahPatientSearchRequest,
  NoahPatientSearchResponse,
  NoahPatientSelectRequest,
  NoahSession,
  SessionReportOptions,
  SessionReportTarget,
  SyncNoahPatientsRequest
} from "./types/noah.type";
import { Patient } from "./types/patients.type";
import type { ReportInput } from "./types/reports.type";

const SESSION_REPORT_OPTIONS_QUERY = "{id,session_date,report_types,reports}";
const SESSION_REPORT_OPTIONS_WITH_SOFTWARE_QUERY =
  "{id,session_date,report_types,reports,noah_actions{module_id}}";

type PatientsApiState = Parameters<
  typeof patientsApi.util.selectCachedArgsForQuery
>[0];

export const extendedNoahApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getNoahPatient: builder.query<NoahPatientResponse, number>({
      query: (patientId: number) => `/noah/${patientId}/noah-es`,
      // The Noah section renders its own inline status alert (ENG-1131), so suppress the
      // global timeout toast on 504 to avoid double-messaging. RUM logging is unaffected.
      extraOptions: { suppressGlobalErrorToast: true },
      providesTags: ["NoahPatient"],
      // The backend verifies the match (name + DOB against Noah ES) while serving this
      // request and returns the resulting `noah_verified`. The header logo and the
      // audiology mismatch alert read that flag from the cached patient, so write it
      // through to every cached projection of this patient. Without this the logo stays
      // red until the next patient fetch, and after a create / match / legacy-id update
      // the parallel patient refetch can land before the verification did.
      async onQueryStarted(patientId, { dispatch, getState, queryFulfilled }) {
        let noahVerified: boolean | undefined;
        try {
          ({
            data: { noah_verified: noahVerified }
          } = await queryFulfilled);
        } catch {
          return;
        }
        if (typeof noahVerified !== "boolean") return;

        const state = getState() as PatientsApiState;
        const cachedArgs = patientsApi.util
          .selectCachedArgsForQuery(state, "getPatient")
          .filter((args) => args.patientId === patientId);

        let hasInFlightRefetch = false;
        for (const args of cachedArgs) {
          const entry = patientsApi.endpoints.getPatient.select(args)(state);
          if (entry.status === QueryStatus.pending) hasInFlightRefetch = true;
          if (entry.data?.noah_verified === noahVerified) continue;
          dispatch(
            patientsApi.util.updateQueryData("getPatient", args, (draft) => {
              draft.noah_verified = noahVerified;
            })
          );
        }

        // A patient refetch that was already in flight read the DB before the
        // verification was saved, and RTK replaces the data we just patched when it
        // lands. Invalidating queues one more fetch until that refetch settles.
        if (hasInFlightRefetch) {
          dispatch(
            patientsApi.util.invalidateTags([
              { type: "Patient", id: patientId }
            ])
          );
        }
      }
    }),
    editNoahLegacyId: builder.mutation<
      void,
      { patient: number; legacy_patient_id: number }
    >({
      query: ({ patient, legacy_patient_id }) => ({
        url: `/noah/${patient}/update-legacy-id/`,
        method: "PATCH",
        body: { legacy_patient_id }
      }),
      invalidatesTags: [
        "NoahPatient",
        "NoahSessions",
        "NoahDevices",
        "Patients",
        "Patient"
      ]
    }),
    syncNoahSessions: builder.mutation<
      void,
      { patient_id: number; current_clinic: number }
    >({
      query: ({ patient_id, current_clinic }) => ({
        url: `/noah-sessions/sync/`,
        params: { patient_id, current_clinic },
        method: "POST"
      }),
      invalidatesTags: ["NoahSessions", "NoahDevices", "Patients", "Patient"]
    }),
    getNoahEventSubscriptions: builder.query<
      NoahEventSubscriptionsResponse,
      { tenant_id: string }
    >({
      query: ({ tenant_id }) => ({
        url: `/noah-event-subscriptions/`,
        params: { tenant_id },
        method: "GET"
      }),
      providesTags: ["NoahEventSubscriptions"]
    }),
    syncNoahEventSubscriptions: builder.mutation<{ message: string }, string>({
      query: (tenant_id) => ({
        url: `/noah-event-subscriptions/`,
        body: { tenant_id },
        method: "POST"
      }),
      invalidatesTags: ["NoahEventSubscriptions"]
    }),
    deleteNoahEventSubscription: builder.mutation<
      { message: string },
      { event_subscription_id: string; tenant_id: string }
    >({
      query: ({ event_subscription_id, tenant_id }) => ({
        url: `/noah-event-subscriptions/${event_subscription_id}/`,
        body: { tenant_id },
        method: "DELETE"
      }),
      invalidatesTags: ["NoahEventSubscriptions"]
    }),
    installNoahApp: builder.mutation<
      InstallNoahAppResponse,
      { tenant_id?: string } | void
    >({
      query: (body) => ({
        url: `/noah-app-connections/`,
        method: "POST",
        body: body ?? {}
      }),
      invalidatesTags: ["NoahAppConnections"]
    }),
    getNoahAppConnections: builder.query<
      NoahAppConnectionsResponse,
      { tenant_id?: string }
    >({
      query: ({ tenant_id } = {}) => ({
        url: `/noah-app-connections/`,
        params: tenant_id ? { tenant_id } : undefined,
        method: "GET"
      }),
      providesTags: ["NoahAppConnections"]
    }),
    syncNoahPatients: builder.mutation<
      { message: string },
      SyncNoahPatientsRequest
    >({
      query: (request) => ({
        url: `/noah-patient-matching/`,
        method: "POST",
        body: request
      })
    }),
    getNoahSessions: builder.query<NoahSession[], number>({
      query: (patient_id) => ({
        url: `/noah-sessions/`,
        params: { patient_id },
        method: "GET"
      }),
      providesTags: (result, _, patient_id) => [
        "NoahSessions",
        { id: patient_id, type: "NoahSessions" }
      ]
    }),
    getNoahSessionReportOptions: builder.query<
      SessionReportOptions[],
      { patientId: number; withSoftware?: boolean }
    >({
      query: ({ patientId, withSoftware = false }) => ({
        url: `/noah-sessions/`,
        params: {
          patient_id: patientId,
          query: withSoftware
            ? SESSION_REPORT_OPTIONS_WITH_SOFTWARE_QUERY
            : SESSION_REPORT_OPTIONS_QUERY
        },
        method: "GET"
      }),
      providesTags: (result, _, { patientId }) => [
        "NoahSessions",
        { id: patientId, type: "NoahSessions" }
      ]
    }),
    ensureNoahSessionReport: builder.mutation<
      EnsuredSessionReport,
      SessionReportTarget & {
        patient: number;
        clinic?: number;
        defaults?: Partial<ReportInput>;
      }
    >({
      query: ({ sessionId, reportType, patient, clinic, defaults }) => ({
        url: `/noah-sessions/${sessionId}/report/`,
        method: "POST",
        body: { report_type: reportType, patient, clinic, defaults }
      }),
      invalidatesTags: (result, _, { patient }) => [
        "Reports",
        "NoahDevices",
        { id: patient, type: "NoahSessions" },
        ...(result ? [{ type: "Report" as const, id: result.report.id }] : [])
      ]
    }),
    createNoahPatient: builder.mutation<Patient, number>({
      query: (patientId) => ({
        url: `/noah/${patientId}/noah-es`,
        method: "POST"
      }),
      invalidatesTags: ["NoahPatient", "Patients", "Patient", "NoahSessions"]
    }),
    searchNoahPatients: builder.query<
      NoahPatientSearchResponse,
      NoahPatientSearchRequest
    >({
      query: ({ patientId, firstName, lastName, birthdate }) => ({
        url: `/noah/${patientId}/noah-es/match`,
        method: "GET",
        params: {
          ...(firstName !== undefined ? { first_name: firstName } : {}),
          ...(lastName !== undefined ? { last_name: lastName } : {}),
          ...(birthdate !== undefined ? { birthdate } : {})
        }
      })
    }),
    selectNoahPatient: builder.mutation<
      NoahPatientResponse,
      NoahPatientSelectRequest
    >({
      query: ({ patientId, noahPatientId, legacyPatientId }) => ({
        url: `/noah/${patientId}/noah-es/match`,
        method: "POST",
        body: {
          patient_id: noahPatientId,
          ...(legacyPatientId !== undefined
            ? { legacy_patient_id: legacyPatientId }
            : {})
        }
      }),
      invalidatesTags: [
        "NoahPatient",
        "NoahSessions",
        "NoahDevices",
        "Patients",
        "Patient"
      ]
    }),
    openPatientInNoah: builder.mutation<NoahIntegrationRequest, string>({
      query: (patientNoahId) => ({
        url: `https://localhost:8090/NoahWebIntegrationGUIStart/noah4/patientguid/${patientNoahId}`,
        method: "GET",
        headers: {
          Accept: "text/plain"
        },
        mode: "no-cors"
      })
    }),
    getNoahDevices: builder.query<NoahDevice[], number>({
      query: (patient_id) => ({
        url: `/noah-devices/`,
        params: { patient: patient_id },
        method: "GET"
      }),
      providesTags: ["NoahDevices"]
    })
  })
});

export const {
  useGetNoahPatientQuery,
  useEditNoahLegacyIdMutation,
  useCreateNoahPatientMutation,
  useGetNoahSessionsQuery,
  useGetNoahSessionReportOptionsQuery,
  useEnsureNoahSessionReportMutation,
  useSyncNoahSessionsMutation,
  useOpenPatientInNoahMutation,
  useSyncNoahEventSubscriptionsMutation,
  useSyncNoahPatientsMutation,
  useInstallNoahAppMutation,
  useGetNoahAppConnectionsQuery,
  useDeleteNoahEventSubscriptionMutation,
  useGetNoahEventSubscriptionsQuery,
  useGetNoahDevicesQuery,
  useLazySearchNoahPatientsQuery,
  useSelectNoahPatientMutation
} = extendedNoahApi;
